import React from 'react';
import { Check, Group, Image, ImagePlus, MousePointer2, Redo2, Trash2, Type, Undo2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface StudioEditToolbarProps {
  canUndo: boolean;
  canRedo: boolean;
  selectedCount: number;
  onSelectTool: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onDelete: () => void;
  onGroup: () => void;
  onReplace: () => void;
  onCancel: () => void;
  onSave: () => void;
}

/** The bar over the player controls while a scene is being edited: tools, history, actions, Cancel and Done. */
export const StudioEditToolbar: React.FC<StudioEditToolbarProps> = ({
  canUndo,
  canRedo,
  selectedCount,
  onSelectTool,
  onUndo,
  onRedo,
  onDelete,
  onGroup,
  onReplace,
  onCancel,
  onSave,
}) => (
  <div className="flex-shrink-0 h-11 flex items-center justify-center gap-1 px-4 studio-toolbar-bar">
    <TooltipProvider delayDuration={300}>
      {/* Tools */}
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={onSelectTool}
            className="w-8 h-8 rounded-lg flex items-center justify-center bg-accent text-accent-foreground"
          >
            <MousePointer2 className="w-4 h-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent>Select</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <button disabled className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-300 dark:text-zinc-600 cursor-not-allowed">
            <Type className="w-4 h-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent>Text</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <button disabled className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-300 dark:text-zinc-600 cursor-not-allowed">
            <Image className="w-4 h-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent>Image</TooltipContent>
      </Tooltip>

      <div className="w-px h-6 bg-gray-300/50 dark:bg-zinc-600/50 mx-2" />

      {/* History */}
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={onUndo}
            disabled={!canUndo}
            className={cn(
              "w-8 h-8 rounded-lg flex items-center justify-center transition-colors",
              canUndo ? "studio-text-secondary hover:bg-black/5 dark:hover:bg-white/5" : "text-gray-300 dark:text-zinc-700 cursor-not-allowed"
            )}
          >
            <Undo2 className="w-4 h-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent>Undo</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={onRedo}
            disabled={!canRedo}
            className={cn(
              "w-8 h-8 rounded-lg flex items-center justify-center transition-colors",
              canRedo ? "studio-text-secondary hover:bg-black/5 dark:hover:bg-white/5" : "text-gray-300 dark:text-zinc-700 cursor-not-allowed"
            )}
          >
            <Redo2 className="w-4 h-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent>Redo</TooltipContent>
      </Tooltip>

      <div className="w-px h-6 bg-gray-300/50 dark:bg-zinc-600/50 mx-2" />

      {/* Actions */}
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={onDelete}
            disabled={selectedCount === 0}
            className={cn(
              "w-8 h-8 rounded-lg flex items-center justify-center transition-colors",
              selectedCount > 0 ? "studio-text-secondary hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-950/50" : "text-gray-300 dark:text-zinc-700 cursor-not-allowed"
            )}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent>Delete</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={onGroup}
            disabled={selectedCount < 2}
            className={cn(
              "w-8 h-8 rounded-lg flex items-center justify-center transition-colors",
              selectedCount >= 2 ? "studio-text-secondary hover:bg-black/5 dark:hover:bg-white/5" : "text-gray-300 dark:text-zinc-700 cursor-not-allowed"
            )}
          >
            <Group className="w-4 h-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent>Group</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={onReplace}
            disabled={selectedCount !== 1}
            className={cn(
              "w-8 h-8 rounded-lg flex items-center justify-center transition-colors",
              selectedCount === 1 ? "studio-text-secondary hover:bg-black/5 dark:hover:bg-white/5" : "text-gray-300 dark:text-zinc-700 cursor-not-allowed"
            )}
          >
            <ImagePlus className="w-4 h-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent>Replace</TooltipContent>
      </Tooltip>

      <div className="w-px h-6 bg-gray-300/50 dark:bg-zinc-600/50 mx-2" />

      {/* Exit */}
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={onCancel}
            className="w-8 h-8 rounded-lg flex items-center justify-center studio-text-secondary hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-950/50 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent>Cancel</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={onSave}
            className="h-8 px-4 rounded-lg flex items-center justify-center gap-1.5 studio-ai-button text-sm font-medium"
          >
            <Check className="w-4 h-4" />
            <span>Done</span>
          </button>
        </TooltipTrigger>
        <TooltipContent>Save changes</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  </div>
);
